<template>
    <PopoverGroup class="absolute inset-y-0 right-4 flex items-center">
        <Popover id="nav-bar-user" class="relative" v-slot="{ open }">
            <PopoverButton
                :class="[
                    open
                        ? shopAsCustomer
                            ? 'text-nutreance-green-darker'
                            : 'text-nutreance-brown'
                        : shopAsCustomer
                          ? 'text-nutreance-green'
                          : 'text-nutreance-brown-lighter',
                    shopAsCustomer
                        ? 'hover:text-nutreance-green-darker animate-pulse'
                        : 'hover:text-nutreance-brown',
                    'group bg-navbar-brown rounded-md flex items-center text-base font-medium focus:outline-none p-2 mr-2',
                ]"
            >
                <span class="sr-only">Open user menu</span>
                <svg-vue
                    class="h-6 w-6 rounded-full"
                    icon="avatar"
                    aria-hidden="true"
                />
            </PopoverButton>

            <transition
                enter-active-class="transition ease-out duration-200"
                enter-from-class="opacity-0 translate-y-1"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition ease-in duration-150"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 translate-y-1"
            >
                <PopoverPanel
                    :class="[
                        shopAsCustomer ? 'sm:max-w-sm' : '',
                        'absolute z-20 -right-10 sm:right-0 mt-3 transform w-screen max-w-xs',
                    ]"
                >
                    <div
                        class="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden"
                    >
                        <div
                            v-if="!isGuest"
                            :class="[
                                shopAsCustomer
                                    ? 'bg-nutreance-green pb-0'
                                    : canShopAsAnyCustomer
                                      ? 'bg-gray-50 pb-0'
                                      : 'bg-gray-50 pb-2 sm:pb-4',
                                'px-5 pt-2 sm:px-8 sm:pt-4',
                            ]"
                        >
                            <div class="-mx-3 p-3 flow-root rounded-md">
                                <div class="flex items-center">
                                    <span
                                        v-if="!shopAsCustomer"
                                        class="text-base font-medium text-gray-900 ml-3"
                                        >Hello, {{ userName }}!</span
                                    >
                                    <span
                                        v-else
                                        class="text-base font-medium text-white ml-3"
                                        >You're shopping as
                                        <a
                                            id="shop-as-customer-link"
                                            :href="`/user/customers/${shopAsCustomer}`"
                                            class="underline hover:text-gray-100"
                                            >{{ shoppingAs }}</a
                                        >.</span
                                    >
                                </div>
                            </div>
                        </div>

                        <div
                            v-if="shopAsCustomer"
                            class="px-5 bg-nutreance-green py-2 sm:px-8 sm:py-4"
                        >
                            <form
                                action="/user/customers/shop-as"
                                method="POST"
                                class="flow-root -mx-3"
                            >
                                <input
                                    type="hidden"
                                    name="_token"
                                    :value="csrf"
                                />
                                <input
                                    type="hidden"
                                    name="_method"
                                    value="DELETE"
                                />

                                <button
                                    name="forget"
                                    type="submit"
                                    class="p-3 rounded-md hover:bg-nutreance-green-darker inline-flex items-center w-full focus:outline-none"
                                >
                                    <LogoutIcon
                                        class="shrink-0 h-6 w-6 text-white"
                                        aria-hidden="true"
                                    />
                                    <span
                                        class="text-base font-medium text-white ml-3"
                                        >Forget Customer</span
                                    >
                                </button>
                            </form>
                        </div>

                        <div
                            v-if="canShopAsAnyCustomer && !shopAsCustomer"
                            class="px-5 bg-gray-50 py-2 sm:px-8 sm:py-4"
                        >
                            <form
                                action="/user/customers/shop-as"
                                method="POST"
                                class="flow-root -mx-3"
                            >
                                <input
                                    type="hidden"
                                    name="_token"
                                    :value="csrf"
                                />
                                <input
                                    type="hidden"
                                    name="_method"
                                    value="PATCH"
                                />

                                <button
                                    name="shop_as_customer"
                                    type="submit"
                                    class="p-3 rounded-md hover:bg-gray-100 inline-flex items-center w-full focus:outline-none"
                                >
                                    <ShoppingCartSolidIcon
                                        class="shrink-0 h-6 w-6 text-gray-400"
                                        aria-hidden="true"
                                    />
                                    <span
                                        class="text-base font-medium text-gray-900 ml-3"
                                        >Shop as New Customer</span
                                    >
                                </button>
                            </form>
                        </div>

                        <div
                            class="relative grid gap-6 bg-white px-5 py-6 sm:gap-8 sm:p-8"
                        >
                            <a
                                v-if="isGuest"
                                href="/register"
                                class="-m-3 p-3 flex items-center rounded-lg hover:bg-gray-50"
                            >
                                <div
                                    class="shrink-0 flex items-center justify-center h-10 w-10 rounded-md bg-nutreance-green text-white sm:h-12 sm:w-12"
                                >
                                    <UserAddIcon
                                        class="h-7 w-7"
                                        aria-hidden="true"
                                    />
                                </div>

                                <div class="ml-4">
                                    <p class="mt-1 text-sm text-gray-500">
                                        New to Nutreance?
                                    </p>
                                    <p
                                        class="text-base font-medium text-gray-900"
                                    >
                                        Create an Account
                                    </p>
                                </div>
                            </a>

                            <a
                                v-else
                                v-for="item in allowedUserLinks"
                                :key="item.name"
                                :href="item.href"
                                class="-m-3 p-3 flex items-center rounded-lg hover:bg-gray-50"
                            >
                                <div
                                    class="shrink-0 flex items-center justify-center h-10 w-10 rounded-md bg-nutreance-green text-white sm:h-12 sm:w-12"
                                >
                                    <component
                                        :is="item.icon"
                                        class="h-7 w-7"
                                        aria-hidden="true"
                                    />
                                </div>

                                <div class="ml-4">
                                    <p
                                        class="text-base font-medium text-gray-900"
                                    >
                                        {{ item.name }}
                                    </p>
                                </div>
                            </a>
                        </div>

                        <div class="px-5 bg-gray-50 py-2 sm:px-8 sm:py-4">
                            <a
                                :href="isGuest ? '/login' : '/logout'"
                                class="-mx-3 p-3 flow-root rounded-md hover:bg-gray-100"
                            >
                                <div class="flex items-center">
                                    <component
                                        :is="isGuest ? LoginIcon : LogoutIcon"
                                        class="shrink-0 h-6 w-6 text-gray-400"
                                        aria-hidden="true"
                                    />
                                    <span
                                        class="text-base font-medium text-gray-900 ml-3"
                                        >{{
                                            isGuest ? "Sign In" : "Sign Out"
                                        }}</span
                                    >
                                </div>
                            </a>
                        </div>
                    </div>
                </PopoverPanel>
            </transition>
        </Popover>

        <button
            v-if="showCart"
            id="cart-side-bar-toggle"
            class="bg-navbar-brown p-1 rounded-full text-nutreance-brown-lighter hover:text-nutreance-brown focus:outline-none"
            @click="openCartSideBar"
        >
            <span class="sr-only">View shopping cart</span>
            <span class="flex">
                <ShoppingCartOutlineIcon
                    v-if="cartIsEmpty"
                    class="h-6 w-6"
                    aria-hidden="true"
                />
                <ShoppingCartSolidIcon
                    v-else
                    class="h-6 w-6"
                    aria-hidden="true"
                />
                <span
                    class="hidden sm:inline-flex items-center px-3 py-0.5 rounded-full text-sm font-semibold bg-white nutreance-brown"
                >
                    {{ state.cartQuantity }}
                </span>
            </span>
        </button>
    </PopoverGroup>
</template>

<script setup lang="ts">
import { computed, inject, onMounted } from "vue";
import {
    Popover,
    PopoverButton,
    PopoverGroup,
    PopoverPanel,
} from "@headlessui/vue";
import {
    ArchiveIcon,
    CalendarIcon,
    CheckCircleIcon,
    CollectionIcon,
    CreditCardIcon,
    LocationMarkerIcon,
    LoginIcon,
    LogoutIcon,
    ShoppingCartIcon as ShoppingCartOutlineIcon,
    TemplateIcon,
    TicketIcon,
    UserAddIcon,
} from "@heroicons/vue/outline";
import { ShoppingCartIcon as ShoppingCartSolidIcon } from "@heroicons/vue/solid";
import { useCart } from "../../functions/useCart";
import type { AppEmitter } from "../../emitter";

const userLinks = [
    {
        availableFor: ["employee", "superAdmin"],
        name: "Dashboard",
        href: "/user/dashboard",
        icon: TemplateIcon,
    },
    {
        availableFor: ["shipper", "superAdmin"],
        name: "Shipment Batches",
        href: "/user/shipment-batches",
        icon: ArchiveIcon,
    },
    {
        availableFor: ["customer", "employee", "superAdmin"],
        name: "Orders",
        href: "/user/orders",
        icon: CollectionIcon,
    },
    {
        availableFor: ["customer", "employee", "superAdmin"],
        name: "Subscriptions",
        href: "/user/subscriptions",
        icon: CalendarIcon,
    },
    {
        availableFor: ["customer", "employee", "superAdmin"],
        name: "Addresses",
        href: "/user/addresses",
        icon: LocationMarkerIcon,
    },
    {
        availableFor: ["customer", "employee", "superAdmin"],
        name: "Payment Methods",
        href: "/user/payment-methods",
        icon: CreditCardIcon,
    },
    {
        availableFor: ["reviewModerator"],
        name: "Review Moderation",
        href: "/user/reviews/moderation",
        icon: CheckCircleIcon,
    },
    {
        availableFor: ["customer"],
        name: "Coupons",
        href: "/user/coupons",
        icon: TicketIcon,
    },
];

const props = defineProps<{
    csrf?: string;
    initialCartQuantity?: number;
    isCustomer?: string;
    isGuest?: string;
    userName?: string;
    canShopAsAnyCustomer?: string;
    canSeeAnyShipmentBatch?: string;
    canModerateReviews?: string;
    shopAsCustomer?: string;
    shoppingAs?: string;
    showCart?: string;
}>();

const emitter = inject<AppEmitter>("mitt")!;
const { cartIsEmpty, state } = useCart();

const role = computed(() => {
    if (!props.canShopAsAnyCustomer) {
        return "customer";
    }

    if (props.canSeeAnyShipmentBatch && !props.canShopAsAnyCustomer) {
        return "shipper";
    }

    return props.canSeeAnyShipmentBatch && props.canShopAsAnyCustomer
        ? "superAdmin"
        : "employee";
});

// Moderating reviews is a permission of its own, held by roles the coarse `role` above does not
// tell apart, so a link names an audience the viewer belongs to rather than their single role.
const audiences = computed(() =>
    props.canModerateReviews ? [role.value, "reviewModerator"] : [role.value],
);

const allowedUserLinks = computed(() =>
    userLinks.filter((link) =>
        link.availableFor.some((audience) =>
            audiences.value.includes(audience),
        ),
    ),
);

state.cartQuantity = props.initialCartQuantity;

onMounted(() => {
    emitter.on("cartItemUpdated", (data: any) => {
        state.cartQuantity = data.cartQuantity;
    });
});

function openCartSideBar(): void {
    emitter.emit("openCartSideBar");
}
</script>
